import {
  EnergyApiFilter,
  EnergyPowerApiResponse,
  Equipment,
  EquipmentEnergy,
  NormalizedTSEquipmentRecentPower,
  TimeSeriesAggregationLevel,
} from '@energybox/react-ui-library/dist/types';
import {
  formatDateForTimeSeriesApi,
  normalizeTSFetchRecentPowerByEquipment,
} from '@energybox/react-ui-library/dist/utils';
import { Service } from '../config';
import { fetchApi } from '../middleware/apiMiddleware';

export enum Actions {
  TS_ENERGY_BY_EQUIPMENT_ID_SUCCESS = '@@TS_energy/TS_ENERGY_BY_EQUIPMENT_ID_SUCCESS',
  TS_ENERGY_BY_EQUIPMENT_ID_LOADING = '@@TS_energy/TS_ENERGY_BY_EQUIPMENT_ID_LOADING',
  TS_ENERGY_BY_EQUIPMENT_ID_ERROR = '@@TS_energy/TS_ENERGY_BY_EQUIPMENT_ID_ERROR',

  TS_ENERGY_BY_EQUIPMENTS_LOADING = '@@TS_energy/TS_ENERGY_BY_EQUIPMENTS_LOADING',
  TS_ENERGY_BY_EQUIPMENTS_SUCCESS = '@@TS_energy/TS_ENERGY_BY_EQUIPMENTS_SUCCESS',
  TS_ENERGY_BY_EQUIPMENTS_ERROR = '@@TS_energy/TS_ENERGY_BY_EQUIPMENTS_ERROR',

  TS_GET_EQUIPMENT_POWER_BY_SITE_ID_LOADING = '@@energy/TS_GET_EQUIPMENT_POWER_BY_SITE_ID_LOADING',
  TS_GET_EQUIPMENT_POWER_BY_SITE_ID_ERROR = '@@energy/TS_GET_EQUIPMENT_POWER_BY_SITE_ID_ERROR',
  TS_GET_EQUIPMENT_POWER_BY_SITE_ID_SUCCESS = '@@energy/TS_GET_EQUIPMENT_POWER_BY_SITE_ID_SUCCESS',

  TS_GET_SITE_ENERGY_CONSUMPTION_LOADING = '@@energy/TS_GET_SITE_ENERGY_CONSUMPTION_LOADING',
  TS_GET_SITE_ENERGY_CONSUMPTION_ERROR = '@@energy/TS_GET_SITE_ENERGY_CONSUMPTION_ERROR',
  TS_GET_SITE_ENERGY_CONSUMPTION_SUCCESS = '@@energy/TS_GET_SITE_ENERGY_CONSUMPTION_SUCCESS',
}

const baseUrl = '/energy';

export const setEnergyFilter = (filter?: EnergyApiFilter): string => {
  const queryParams = new URLSearchParams();

  //filter by equipmentId
  if (filter && filter.id) {
    queryParams.set('id', filter.id.toString());
  }

  if (filter && filter.from) {
    queryParams.set('from', filter.from.toString());
  }

  if (filter && filter.to) {
    queryParams.set('to', filter.to.toString());
  }

  if (filter && filter.aggregationLevel) {
    queryParams.set('agg', filter.aggregationLevel.toString());
  }

  if (filter && filter.seeSpilloverPoint) {
    queryParams.set('seeSpilloverPoint', filter.seeSpilloverPoint.toString());
  }

  return queryParams.toString();
};

//IMPORTANT: all from/to time queryParams for these functions
//expected to be initially epoch timestamp (in seconds),
//must convert to ISO 8601 format for TS API

const convertQueryParamsToTsApiFormat = (queryParams: EnergyApiFilter) => {
  const updatedQueryParams = { ...queryParams };

  Object.keys(queryParams).forEach(param => {
    const paramValue = queryParams[param];
    if (param === 'to' || param === 'from') {
      const dateFormat = new Date(paramValue * 1000);
      updatedQueryParams[param] = formatDateForTimeSeriesApi(dateFormat);
    } else if (param === 'aggregationLevel') {
      updatedQueryParams[param] = TimeSeriesAggregationLevel[paramValue];
    } else {
      updatedQueryParams[param] = queryParams[param];
    }
  });

  return updatedQueryParams;
};

export const getTSEnergyByEquipmentId = (
  equipmentId: number | string,
  queryParams: EnergyApiFilter,
  idAffix?: string
) => ({
  type: 'API_GET',
  service: Service.timeSeries,
  path: `${baseUrl}/equipment/${equipmentId}/series?${setEnergyFilter(
    convertQueryParamsToTsApiFormat(queryParams)
  )}`,
  loading: {
    type: Actions.TS_ENERGY_BY_EQUIPMENT_ID_LOADING,
    equipmentId: idAffix ? equipmentId + idAffix : equipmentId,
  },
  success: {
    type: Actions.TS_ENERGY_BY_EQUIPMENT_ID_SUCCESS,
    equipmentId: idAffix ? equipmentId + idAffix : equipmentId,
  },
  error: {
    type: Actions.TS_ENERGY_BY_EQUIPMENT_ID_ERROR,
    equipmentId: idAffix ? equipmentId + idAffix : equipmentId,
  },
});

const fetchTSEquipmentEnergyById = (
  equipmentId: number | string,
  queryParams: EnergyApiFilter
): Promise<EnergyPowerApiResponse> => {
  return fetchApi({
    endpoint: `${baseUrl}/equipment/${equipmentId}/series?${setEnergyFilter(
      convertQueryParamsToTsApiFormat(queryParams)
    )}`,
  });
};

export const dontGetTSEnergyByEquipmentIds = () => ({
  type: Actions.TS_ENERGY_BY_EQUIPMENTS_ERROR,
});

export const getTSEnergyByEquipmentIds = (
  equipments: Equipment[],
  fromDateTimestamp: number,
  toDateTimestamp: number
) => dispatch => {
  dispatch({
    type: Actions.TS_ENERGY_BY_EQUIPMENTS_LOADING,
  });

  const fetches = equipments.map(equipment => {
    return fetchTSEquipmentEnergyById(equipment.id, {
      from: fromDateTimestamp.toFixed(0),
      to: toDateTimestamp.toFixed(0),
    })
      .then(res => {
        return {
          ...res,
          equipmentId: equipment.id,
        };
      })
      .catch(err => console.log(err));
  });

  Promise.all<EquipmentEnergy | void>(fetches).then(res => {
    const validResults: EquipmentEnergy[] = res.filter(
      result => result !== undefined
    ) as EquipmentEnergy[];
    if (validResults.length > 0) {
      dispatch({
        type: Actions.TS_ENERGY_BY_EQUIPMENTS_SUCCESS,
        payload: validResults,
      });
    } else {
      dispatch({
        type: Actions.TS_ENERGY_BY_EQUIPMENTS_ERROR,
      });
    }
  });
};

export const fetchTSRecentPowerByEquipment = (
  equipmentId: number
): Promise<NormalizedTSEquipmentRecentPower> =>
  fetchApi({
    service: Service.timeSeries,
    endpoint: `${baseUrl}/equipment/${equipmentId}/recent-power`,
  }).then(data => {
    return normalizeTSFetchRecentPowerByEquipment(data);
  });

export const getTSEquipmentEnergyPowerBySiteId = (siteId: number) => ({
  type: 'API_GET',
  service: Service.timeSeries,
  path: `${baseUrl}/site/${siteId}/equipment/recent-power`,
  loading: { type: Actions.TS_GET_EQUIPMENT_POWER_BY_SITE_ID_LOADING, siteId },
  success: { type: Actions.TS_GET_EQUIPMENT_POWER_BY_SITE_ID_SUCCESS, siteId },
  error: { type: Actions.TS_GET_EQUIPMENT_POWER_BY_SITE_ID_ERROR, siteId },
});

export const getTSSiteEnergyConsumption = (
  siteId: string | number,
  queryParams: EnergyApiFilter,
  key: string
) => ({
  type: 'API_GET',
  service: Service.timeSeries,
  path: `${baseUrl}/site/${siteId}/consumption?${setEnergyFilter(
    convertQueryParamsToTsApiFormat(queryParams)
  )}`,
  loading: {
    type: Actions.TS_GET_SITE_ENERGY_CONSUMPTION_LOADING,
    siteId,
    key,
  },
  success: {
    type: Actions.TS_GET_SITE_ENERGY_CONSUMPTION_SUCCESS,
    siteId,
    key,
  },
  error: { type: Actions.TS_GET_SITE_ENERGY_CONSUMPTION_ERROR, siteId, key },
});
